import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    Paper,
    useTheme,
    useMediaQuery,
    Divider,
    LinearProgress,
    styled,
    Avatar,
    Card,
    CardContent,
    Stack
} from "@mui/material";
import {
    Close as CloseIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    CheckCircle as CheckCircleIcon,
    Home as HomeIcon,
    DeleteOutline,
    Celebration as CelebrationIcon,
    Logout as LogoutIcon,
    Login as LoginIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import RegistrationForm from "./registration";
import DownloadFiles from "../files/downloadFiles";
import ContentThree from "./dialogContent/ContentThree";
import CryptoJS from "crypto-js";
import {ApiDetails, KEY} from "../../Constants";
import {CloudUploadOutlined} from "@material-ui/icons";
import {IconFileExcel} from "@tabler/icons-react";
import LoadingButton from "@mui/lab/LoadingButton";

// Styled components
const StyledDialog = styled(Dialog)(({theme}) => ({
    "& .MuiPaper-root": {
        borderRadius: 12,
        maxWidth: "900px",
        width: "100%"
    }
}));

const StyledStepLabel = styled(StepLabel)(({theme}) => ({
    "& .MuiStepLabel-label": {
        fontWeight: 500,
        fontSize: "0.9rem",
        "&.Mui-active": {
            fontWeight: 700,
            color: theme.palette.primary.main
        },
        "&.Mui-completed": {
            color: theme.palette.success.main
        }
    }
}));

const WelcomeBox = styled(Box)(({theme}) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    textAlign: "center",
    marginBottom: theme.spacing(3)
}));

const SuccessAvatar = styled(Avatar)(({theme}) => ({
    width: 80,
    height: 80,
    backgroundColor: theme.palette.success.main,
    margin: '0 auto',
    marginBottom: theme.spacing(3),
    boxShadow: '0 8px 16px rgba(0,200,83,0.2)'
}));

// Success animation
const SuccessAnimation = styled('div')(({theme}) => ({
    position: 'relative',
    width: '150px',
    height: '150px',
    margin: '0 auto',
    marginBottom: theme.spacing(3),
    '& .checkmark': {
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        display: 'block',
        strokeWidth: 2,
        stroke: '#fff',
        strokeMiterlimit: 10,
        boxShadow: 'inset 0px 0px 0px #4BB543',
        animation: 'fill .4s ease-in-out .4s forwards, scale .3s ease-in-out .9s both',
        position: 'relative',
        top: '50%',
        left: '50%',
        marginTop: '-28px',
        marginLeft: '-28px',
    },
    '& .checkmark__circle': {
        stroke: '#4BB543',
        strokeDasharray: 166,
        strokeDashoffset: 166,
        strokeWidth: 2,
        strokeMiterlimit: 10,
        animation: 'stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards',
    },
    '& .checkmark__check': {
        transformOrigin: '50% 50%',
        strokeDasharray: 48,
        strokeDashoffset: 48,
        animation: 'stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards',
    },
    '@keyframes stroke': {
        '100%': {
            strokeDashoffset: 0,
        },
    },
    '@keyframes scale': {
        '0%, 100%': {
            transform: 'none',
        },
        '50%': {
            transform: 'scale3d(1.1, 1.1, 1)',
        },
    },
    '@keyframes fill': {
        '100%': {
            boxShadow: 'inset 0px 0px 0px 30px #4BB543',
        },
    },
}));

function getSteps() {
    return [
        "Account Details",
        "Welcome",
        "Download Templates",
        "Upload Completed File",
        "Success"
    ];
}

export default function OnboardingDialog(props) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [activeStep, setActiveStep] = useState(0);
    const [setupDone, setSetupDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [stableDetails, setStableDetails] = useState(null);

    // Get user first name for welcome message with proper null checking
    const firstName = stableDetails?.get("first_name") || "User";

    // Stabilize the details prop to prevent empty map issues
    useEffect(() => {
        if (props.details && props.details.size > 0) {
            setStableDetails(props.details);
        }
    }, [props.details]);

    const steps = getSteps();

    useEffect(() => {
        // Check if user has already completed setup
        const isNew = localStorage.getItem("isNew");
        if (isNew === "false") {
            setSetupDone(true);
        }

        console.log("onboardingDialog");
        console.log("Props details:", props.details);
        console.log("Stable details:", stableDetails);
        console.log("First name:", firstName);
    }, [props.details, stableDetails, firstName]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    const handleComplete = () => {
        setSetupDone(true);
        localStorage.setItem("isNew", false);
        // Move to success step instead of showing a snackbar
        setActiveStep(4);
    };

    const handleClose = () => {
        if (!setupDone && activeStep < 4) {
            props.snackBar({
                text: "Please finish setting up your account",
                type: "warning"
            });
        } else {
            // If we're at the success step or setup is done, allow closing
            window.location.href = "/home";
        }
    };

    const handleLogout = () => {
        // Clear localStorage
        localStorage.clear();
        // Redirect to login page
        window.location.href = "/";
    };

    // Render step content based on active step
    function getStepContent(step) {
        // Don't render if we don't have stable details yet
        if (!stableDetails || stableDetails.size === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography>Loading...</Typography>
                </Box>
            );
        }

        switch (step) {
            case 0:
                return (
                    <RegistrationForm
                        details={stableDetails}
                        snackBar={props.snackBar}
                        next={handleNext}
                    />
                );
            case 1:
                return (
                    <Box sx={{p: 2}}>
                        <WelcomeBox>
                            <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
                                Welcome to Pegasus, {firstName}!
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                We're excited to have you on board
                            </Typography>
                        </WelcomeBox>

                        <Paper
                            elevation={0}
                            sx={{
                                p: {xs: 2, sm: 3},
                                mb: 3,
                                bgcolor: "#f8f9fa",
                                borderRadius: 2,
                                mx: 'auto',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                overflow: 'hidden'
                            }}
                        >
                            <Typography variant="body1" paragraph>
                                👋 Thanks for choosing Pegasus! Our platform makes managing your properties a breeze with
                                smart tools designed for landlords like you.
                            </Typography>

                            <Typography variant="body1" paragraph>
                                🔒 Your data's security is our priority. Everything you share here stays private and is
                                only accessible with proper credentials. Remember to only share your login details with
                                people you trust.
                            </Typography>

                            <Typography variant="body1" paragraph>
                                🏠 To help you get the most out of Pegasus, we'll need a few details about your
                                properties. The next few steps will guide you through the process - it's quick and
                                straightforward!
                            </Typography>

                            <Typography variant="body1" sx={{fontWeight: 'medium', mt: 2}}>
                                Ready to get started? Just follow the simple steps in this guide!
                            </Typography>
                        </Paper>
                    </Box>
                );
            case 2:
                return (
                    <Box sx={{p: 2}}>
                        <DownloadFiles
                            details={stableDetails}
                            display={true}
                            next={handleNext}
                            snackBar={props.snackBar}
                        />
                    </Box>
                );
            case 3:
                return (
                    <Box sx={{p: 2}}>
                        <ContentThree
                            snackBar={props.snackBar}
                            details={stableDetails}
                            previous={handleBack}
                            complete={handleComplete}
                        />
                    </Box>
                );
            case 4:
                return (
                    <Box sx={{p: 3, textAlign: 'center'}}>
                        {/* Custom animated checkmark */}
                        <SuccessAnimation>
                            <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                <circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none"/>
                                <path className="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                            </svg>
                        </SuccessAnimation>

                        <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
                            Congratulations, {firstName}!
                        </Typography>

                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Your account setup is complete! 🎉
                        </Typography>

                        <Card sx={{mb: 4, mt: 4, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)'}}>
                            <CardContent sx={{p: 3}}>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary" paragraph>
                                    One Last Step
                                </Typography>

                                <Typography variant="body1" paragraph>
                                    To ensure all your property data is properly loaded, please log out and log back in
                                    to refresh your dashboard.
                                </Typography>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        bgcolor: "#f0f7ff",
                                        borderRadius: 2,
                                        border: '1px solid #bbdefb',
                                        mb: 2
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <RefreshIcon color="primary"/>
                                        <Typography variant="body2">
                                            Logging out and back in will ensure your dashboard shows all your properties
                                            and the latest data.
                                        </Typography>
                                    </Stack>
                                </Paper>

                                <Box sx={{display: 'flex', justifyContent: 'center', mt: 3}}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        startIcon={<LogoutIcon/>}
                                        onClick={handleLogout}
                                        sx={{px: 4, py: 1.2, borderRadius: 2}}
                                    >
                                        Log Out Now
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>

                        <Typography variant="body2" color="text.secondary">
                            You can also continue to the dashboard and log out later if you prefer.
                        </Typography>
                    </Box>
                );
            default:
                return "Unknown step";
        }
    }

    return (
        <StyledDialog
            fullScreen={fullScreen}
            open={true}
            onClose={handleClose}
            maxWidth="lg"
            aria-labelledby="onboarding-dialog-title"
        >
            <DialogTitle sx={{px: 3, pt: 3, pb: 2}} id="onboarding-dialog-title">
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Typography variant="h6" component="div" fontWeight="bold">
                        {activeStep === 0 ? "Complete Your Registration" :
                            activeStep === 1 ? "Welcome to Pegasus" :
                                activeStep === 2 ? "Download Property Templates" :
                                    activeStep === 3 ? "Upload Completed Template" :
                                        "Setup Complete"}
                    </Typography>
                    {setupDone || activeStep === 4 ? (
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <CloseIcon/>
                        </IconButton>
                    ) : null}
                </Box>
            </DialogTitle>

            <Divider/>

            {/* Stepper Navigation */}
            <Box style={{zoom: "80%"}} sx={{px: 3, pt: 3}}>
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel={!isMobile}
                    orientation={isMobile ? "vertical" : "horizontal"}
                >
                    {steps.map((label, index) => {
                        const stepProps = {};

                        return (
                            <Step key={label} {...stepProps}>
                                <StyledStepLabel>{label}</StyledStepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
            </Box>

            {/* Progress indicator */}
            <Box sx={{px: 3, py: 1}}>
                <LinearProgress
                    variant="determinate"
                    value={(activeStep / (steps.length - 1)) * 100}
                    sx={{height: 6, borderRadius: 3}}
                />
            </Box>

            <DialogContent dividers sx={{p: 0}}>
                {getStepContent(activeStep)}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2, justifyContent: "space-between"}}>
                <Button
                    color="inherit"
                    disabled={activeStep === 0 || activeStep === 4}
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon/>}
                    sx={{visibility: (activeStep === 0 || activeStep === 4) ? 'hidden' : 'visible'}}
                >
                    Back
                </Button>

                <Box>
                    {activeStep === steps.length - 1 ? (
                        <></>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            endIcon={<ArrowForwardIcon/>}
                            sx={{display: activeStep === 0 || activeStep === 3 ? 'none' : 'flex'}}
                        >
                            Next
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </StyledDialog>
    );
}

// Fixed PropTypes to match actual usage
OnboardingDialog.propTypes = {
    details: PropTypes.object, // Removed isRequired since it can be null initially
    snackBar: PropTypes.func.isRequired,
    topBar: PropTypes.func
};